import { useMemo, type ReactNode } from "react";
import { NavLink } from "react-router-dom-v5-compat";
import {
  useCachedFeatureFlag,
  useFeatureFlagJsonIgnoreVariant,
} from "@apac/feature-flags";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HomepageBanner, PremierDestinationHomepageBanner } from "@hsbc/portal";
import {
  CARS_PATH,
  FeatureFlagNames,
  FLIGHTS_PATH,
  HOTELS_PATH,
} from "@hsbc/utilities";
import { CarSearch } from "@tenant/cars";
import { FlightSearch } from "@tenant/flights";
import { HotelSearch } from "@tenant/hotels";
import { Page, PageHeader } from "@tenant/ui";
import {
  ConnectedNoticeBanner,
  type NoticesContainer,
} from "./ConnectedNoticeBanner";
import styles from "./Homepage.module.scss";

interface FunnelProps {
  label: string;
  funnelContent: ReactNode;
  path: string;
  funnelName: Funnel;
}

type SupportedFunnel = Funnel.Air | Funnel.Ground | Funnel.Lodging;

export const Homepage = ({ funnel }: { funnel: SupportedFunnel }) => {
  const { t, language } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "Homepage");

  const importantNotices = useFeatureFlagJsonIgnoreVariant<NoticesContainer>(
    FeatureFlagNames.IMPORTANT_NOTICE
  );

  const funnelsMap: Record<SupportedFunnel, FunnelProps> = {
    [Funnel.Lodging]: {
      label: t("core-hotels.funnelName"),
      funnelContent: <HotelSearch />,
      path: HOTELS_PATH,
      funnelName: Funnel.Lodging,
    },
    [Funnel.Air]: {
      label: t("core-flights.funnelName"),
      funnelContent: <FlightSearch />,
      path: FLIGHTS_PATH,
      funnelName: Funnel.Air,
    },
    [Funnel.Ground]: {
      label: t("core-cars.funnelName"),
      funnelContent: <CarSearch />,
      path: CARS_PATH,
      funnelName: Funnel.Ground,
    },
  };

  const { funnelContent } = funnelsMap[funnel];

  const disableHotels = useCachedFeatureFlag(FeatureFlagNames.DISABLE_HOTELS);
  const disableCars = useCachedFeatureFlag(FeatureFlagNames.DISABLE_CARS);
  const disableFlights = useCachedFeatureFlag(FeatureFlagNames.DISABLE_FLIGHTS);

  const tabFunnels = useMemo(() => {
    const funnels = [] as FunnelProps[];
    if (!disableFlights) funnels.push(funnelsMap.Air);
    if (!disableHotels) funnels.unshift(funnelsMap.Lodging);
    if (!disableCars) funnels.push(funnelsMap.Ground);

    return funnels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableHotels, disableCars, disableFlights]);

  return (
    <div className={block}>
      <Page className={cn("page")} header={<PageHeader hideSubHeader />}>
        <div className={cn("container")}>
          <ConnectedNoticeBanner
            notices={importantNotices}
            language={language}
            ctaText={t("home.learnMoreLink")}
          />
          <HomepageBanner />

          {tabFunnels.length > 1 && (
            <div className={cn("funnel-tab")} role="tablist">
              {tabFunnels.map(({ label, path, funnelName }) => (
                <NavLink
                  key={label}
                  to={path}
                  className={cn("funnel-navigation-button", {
                    selected: funnelName === funnel,
                  })}
                  role="tab"
                  aria-selected={funnelName === funnel}
                >
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          )}
          <PremierDestinationHomepageBanner
            className={cn("premier-destination-banner")}
            funnel={funnel}
          />

          <div className={cn("content")}>{funnelContent}</div>
        </div>
      </Page>
    </div>
  );
};

export default Homepage;
